import {translate} from '@docusaurus/Translate';
import {MAX_QUESTION_LENGTH, type PageContext} from './assistantConversation';

export const PAGE_REFERENCE_EVENT = 'folderspan:page-reference';

const privateElements = '[data-assistant-ui], input, textarea, select, [contenteditable]:not([contenteditable="false"]), script, style, noscript, [hidden]';
const ignoredElements = `${privateElements}, [aria-hidden="true"]`;

export function canReferenceElement(element: Element): boolean {
  return !element.closest(privateElements);
}

function referenceUrl(value: string | null, baseUrl: string): string {
  if (!value) {
    return '';
  }
  try {
    const url = new URL(value, baseUrl);
    url.username = '';
    url.password = '';
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

function imageReference(image: Element): string {
  const label = image.getAttribute('alt') || image.getAttribute('title') || translate({message: '页面图片'});
  const url = referenceUrl(image.getAttribute('src'), image.baseURI);
  return translate({message: '图片：{value0}{value1}'}, {value0: label, value1: url ? translate({message: '\n图片地址：{value0}'}, {value0: url}) : ''});
}

export function getElementReference(element: Element): string {
  if (!canReferenceElement(element)) {
    return '';
  }

  const target = element.closest('img')
    ?? element.closest('a, button')
    ?? element.closest('pre, table, p, li, h1, h2, h3, h4, h5, h6, figure, summary, label, section, article, svg')
    ?? element;
  if (!canReferenceElement(target)) {
    return '';
  }
  if (target.tagName === 'IMG') {
    return imageReference(target);
  }

  const clone = target.cloneNode(true) as Element;
  clone.querySelectorAll(ignoredElements).forEach((node) => node.remove());
  clone.querySelectorAll('img').forEach((image) => {
    image.replaceWith(`\n${imageReference(image)}\n`);
  });
  clone.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
  clone.querySelectorAll('td, th').forEach((node) => node.append('\t'));
  clone.querySelectorAll('p, div, li, pre, tr, h1, h2, h3, h4, h5, h6, figure').forEach((node) => node.append('\n'));

  const label = target.getAttribute('aria-label') || target.getAttribute('title') || '';
  let text = (clone.textContent?.trim() || label).replace(/\n{3,}/g, '\n\n');
  const link = target.closest('a');
  const url = referenceUrl(link?.getAttribute('href') ?? null, target.baseURI);
  if (url) {
    text += translate({message: '\n链接地址：{value0}'}, {value0: url});
  }
  return text;
}

export function getSelectionReference(selection: Selection | null): string {
  if (!selection || selection.isCollapsed || !selection.rangeCount) {
    return '';
  }
  const range = selection.getRangeAt(0);
  const ancestor = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer as Element
    : range.commonAncestorContainer.parentElement;
  if (!ancestor || !canReferenceElement(ancestor)) {
    return '';
  }
  for (const element of Array.from(ancestor.querySelectorAll(privateElements))) {
    if (range.intersectsNode(element)) {
      return '';
    }
  }
  return selection.toString().trim();
}

export function appendPageReference(draft: string, context: PageContext, content: string): string | null {
  const quote = content.trim().split('\n').map((line) => `> ${line}`).join('\n');
  const reference = translate({message: '引用自「{value0}」（{value1}）：\n{value2}\n\n'}, {value0: context.title, value1: context.path, value2: quote});
  const result = `${draft.trimEnd()}${draft.trim() ? '\n\n' : ''}${reference}`;
  return result.length <= MAX_QUESTION_LENGTH ? result : null;
}
